import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import isTextEmpty from '@wix/da-editor-tiptap/pkg/components/utils/isEmpty';
import type { EditorState } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import { getMaxPrice } from '@wix/da-shared-react/pkg/redux/shared/selectors';
import getMaxPriceForAnswerChoice from './hooks/useMaxPriceForAnswerChoice';
import {
  QuestionType,
  OriginalDeviationFieldName,
  QuestionFieldName,
} from './types';
import {
  MAX_TITLE_LENGTH,
  MAX_DESCRIPTION_LENGTH,
} from '../../constants';
import { TITLE_REGEX } from '../../fields/TitleField/validation';

// Question text is essentially unlimited
const MAX_QUESTION_LENGTH = MAX_DESCRIPTION_LENGTH;
export const MIN_PRICE = 1;

export function useFirstStepValidation() {
  const { t } = useTranslation();
  const maxPrice = useSelector(getMaxPrice);
  const validationSchema = yup.object({
    // Title validation is lifted from the deviation submit form
    [OriginalDeviationFieldName.TITLE]: yup
      .string()
      .required(t('deviationSubmit.validation.error.title.required'))
      .test(
        'title',
        t('deviationSubmit.validation.error.title.invalid'),
        v => !TITLE_REGEX.test(v)
      )
      .max(MAX_TITLE_LENGTH, t('deviationSubmit.validation.error.tooLong')),

    [OriginalDeviationFieldName.PRICE]: yup
      .number()
      .required(t('originalDeviationPopup.error.price.required'))
      .typeError(t('deviationSubmit.validation.error.type.number'))
      .min(
        MIN_PRICE,
        t('originalDeviationPopup.error.price.invalid', {
          min: MIN_PRICE,
          max: maxPrice,
        })
      )
      .max(
        maxPrice,
        t('originalDeviationPopup.error.price.invalid', {
          min: MIN_PRICE,
          max: maxPrice,
        })
      )
      .integer(t('originalDeviationPopup.error.price.integer')),

    [OriginalDeviationFieldName.DESCRIPTION]: yup
      .object()
      .test(
        'required',
        t('originalDeviationPopup.form.description.required'),
        value => {
          if (!value) {
            return false;
          }
          if (typeof value === 'function') {
            const editorData: EditorState = JSON.parse(value());
            if (editorData) {
              return !isTextEmpty(editorData.document);
            }
          }
          return false;
        }
      ),
  });
  return validationSchema;
}

export function useQuestionFormValidation() {
  const { t } = useTranslation();
  const maxPrice = useSelector(getMaxPriceForAnswerChoice);
  const validationSchema = yup.object({
    [QuestionFieldName.QUESTION_BODY]: yup
      .object()
      .test(
        'required',
        t('originalDeviationPopup.error.question.required'),
        value => {
          if (!value) {
            return false;
          }
          if (typeof value === 'function') {
            const editorData: EditorState = JSON.parse(value());
            if (editorData) {
              return !isTextEmpty(editorData.document);
            }
          }
          return false;
        }
      )
      .test(
        'editor-max-length',
        t('originalDeviationPopup.error.question.tooLong'),
        value => {
          if (!value) {
            return true;
          }
          if (typeof value === 'function') {
            const v = value();
            if (v) {
              // We use half because html and feature are included in the limit
              return v.length < MAX_QUESTION_LENGTH / 2;
            }
          }
          return true;
        }
      ),
    [QuestionFieldName.IS_REQUIRED]: yup.boolean(),
    [QuestionFieldName.QUESTION_TYPE]: yup
      .string()
      .required(t('originalDeviationPopup.error.answerType.required')),
    [QuestionFieldName.ANSWER_CHOICES]: yup
      .array()
      .when(QuestionFieldName.QUESTION_TYPE, {
        is: (type: QuestionType) => type !== 'free_text',
        then: schema =>
          schema
            .of(
              yup.object().shape({
                answer: yup
                  .string()
                  .required(
                    t('originalDeviationPopup.error.answerChoices.required')
                  ),
              })
            )
            .min(
              1,
              t('originalDeviationPopup.error.price.invalid', {
                min: MIN_PRICE,
                max: maxPrice,
              })
            )
            .max(
              maxPrice,
              t('originalDeviationPopup.error.price.invalid', {
                min: MIN_PRICE,
                max: maxPrice,
              })
            )
            .required(t('originalDeviationPopup.error.answerChoices.required')),
        otherwise: schema => schema.notRequired(),
      }),
  });
  return validationSchema;
}
